'use client';

import { useCallback, useEffect, useRef } from 'react';
import {
  type BaseComponentProps,
  type ContentfulImageType,
  type ContentfulTextType,
  findEntryByType,
  type HeadingLevel,
} from '@bedrock/core/contentful';
import Script from 'next/script';
import {
  type ContentfulComposableElementType,
  type ContentfulInlineFrameType,
} from 'lib/contentful/types';
import { LazyWrap } from 'ui/common/lazy-wrap';
import { MediaImage, PageBlock } from 'ui/components';

declare global {
  type AnyRoadOptions = {
    container: string;
    plugin: { id?: string };
  };

  type AnyRoadInstance = {
    triggerContactModal: (selector: string) => void;
  };

  type AnyRoad = new (options: AnyRoadOptions) => AnyRoadInstance;

  var AnyRoad: AnyRoad | undefined;
}

const SCRIPT_ID = 'anyroad-integration-script';
const ANY_ROAD_CONTAINER_ID = 'anyroad-container';
const ANYROAD_PLUGIN_ID = process.env.NEXT_PUBLIC_SITEURL?.includes('stage')
  ? 'test-experience-74'
  : 'guinness-open-gate-brewery-chicago';

type AnyRoadIntegrationProps = BaseComponentProps<ContentfulComposableElementType>;

function AnyRoadIntegration({ fields, dictionary }: Readonly<AnyRoadIntegrationProps>) {
  const initCalledRef = useRef<AnyRoadInstance>(null);
  const inlineFrame = findEntryByType<ContentfulInlineFrameType>(fields.elements, 'inlineFrame');
  const image = findEntryByType<ContentfulImageType>(fields.elements, 'image');
  const text = findEntryByType<ContentfulTextType>(fields.elements, 'text');
  const initAnyRoad = useCallback(() => {
    if (initCalledRef.current) return;
    if (typeof globalThis.AnyRoad !== 'function') return;

    try {
      initCalledRef.current = new globalThis.AnyRoad({
        container: `#${ANY_ROAD_CONTAINER_ID}`,
        plugin: { id: ANYROAD_PLUGIN_ID },
      });

      if (document.querySelector('#contact-form')) {
        initCalledRef.current.triggerContactModal('#contact-form');
      }
    } catch (e) {
      console.error('AnyRoad init error:', e);
    }
  }, []);

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (typeof globalThis.AnyRoad === 'function') {
      initAnyRoad();
    }
  }, [initAnyRoad]);

  if (!inlineFrame) {
    return null;
  }

  const Headline = (text?.headingLevel?.toLowerCase() ?? 'h2') as Lowercase<HeadingLevel>;

  return (
    <div className="relative">
      <MediaImage classNames={{ root: 'z-0', image: 'max-2xl:object-contain' }} image={image} />
      <PageBlock>
        <section aria-label={inlineFrame.title} className="relative z-10">
          <Headline className="br-type-title-2xl md:br-type-title-4xl text-foreground text-center">
            {text?.heading}
          </Headline>
          <div className="min-h-[620px] md:min-h-[510px]">
            <div
              ref={containerRef}
              className="mx-auto mt-[var(--spacing-4xl)] min-h-[620px] max-w-[820px] md:min-h-[510px]"
              id={ANY_ROAD_CONTAINER_ID}
            />
            <LazyWrap>
              <Script
                id={SCRIPT_ID}
                onLoad={initAnyRoad}
                src="https://app.anyroad.com/assets/integration-v1.1.js"
                strategy="lazyOnload"
              />
            </LazyWrap>
          </div>
          <div className="mt-[-18px] flex items-center justify-center">
            <a
              className="text-foreground br-type-body-sm text-center text-[14px] no-underline"
              href={`https://www.anyroad.com?utm_source=${ANYROAD_PLUGIN_ID}&utm_medium=PBA&utm_campaign=PLUGIN`}
              rel="noopener noreferrer"
              target="_blank"
            >
              {dictionary.poweredByAnyRoad || 'Powered by AnyRoad'}
            </a>
          </div>
        </section>
      </PageBlock>
    </div>
  );
}

export default AnyRoadIntegration;
